import React from "react";
import { Link } from "react-router-dom";
import {
  Heart, Repeat, UserPlus, BookOpen, GraduationCap, Building2,
  ShoppingBag, Gift, Users, Compass, Sparkles, HandHeart, ShieldCheck, ArrowRight, ExternalLink,
} from "lucide-react";
import { DonateButton } from "@/components/DonateButton";
import { PRINTIFY_URL } from "@/lib/config";

const WAYS = [
  { icon: Heart, title: "One-Time Gift", desc: "Make an immediate impact with a secure tax-deductible gift.", action: "donate", available: true },
  { icon: Repeat, title: "Monthly Giving", desc: "Sustain a girl's journey all year long.", action: "future", available: false },
  { icon: UserPlus, title: "Sponsor a Girl", desc: "Underwrite a girl's full program experience.", action: "future", available: false },
  { icon: BookOpen, title: "Sponsor a Workshop", desc: "Fund a complete confidence or life-readiness workshop.", action: "future", available: false },
  { icon: GraduationCap, title: "Sponsor a College Tour", desc: "Help a girl picture her future on a real campus.", action: "future", available: false },
  { icon: Building2, title: "Corporate Sponsorship", desc: "Align your brand with girls' empowerment.", action: "partner", available: true },
  { icon: ShoppingBag, title: "Shop AGG", desc: "Wear the mission — every purchase gives back.", action: "shop", available: true },
  { icon: Gift, title: "Donate Supplies", desc: "Contribute journals, kits, and program materials.", action: "future", available: false },
  { icon: Users, title: "Volunteer", desc: "Give your time and presence to AGG girls.", action: "volunteer", available: true },
  { icon: HandHeart, title: "Mentor", desc: "Be the woman who believes in her.", action: "mentor", available: true },
];

const OUTCOMES = [
  { icon: Sparkles, label: "Build Confidence" },
  { icon: Users, label: "Develop Leadership" },
  { icon: Compass, label: "Learn Life Readiness" },
  { icon: GraduationCap, label: "Explore College" },
  { icon: BookOpen, label: "Explore Careers" },
  { icon: Heart, label: "Build Healthy Relationships" },
  { icon: HandHeart, label: "Find Positive Mentors" },
  { icon: ShieldCheck, label: "Believe In Themselves" },
];

const WayCard = ({ way }) => {
  const Icon = way.icon;
  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className="h-12 w-12 rounded-2xl bg-[#FFF5F9] flex items-center justify-center group-hover:bg-[#E94F9A] transition-colors duration-300">
          <Icon className="h-5 w-5 text-[#E94F9A] group-hover:text-white transition-colors duration-300" />
        </div>
        {!way.available && (
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[#C8A96A] bg-[#FFF5F9] px-2.5 py-1 rounded-full">{"Coming Soon"}</span>
        )}
      </div>
      <h3 className="font-serif text-lg font-bold text-[#4B1E5A] mt-5">{way.title}</h3>
      <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed mt-1.5 flex-grow">{way.desc}</p>
      {way.available && (
        <span className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold text-[#E94F9A] mt-4 group-hover:gap-2.5 transition-all">
          {"Learn More"} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      )}
    </>
  );

  const baseCls = "group flex flex-col bg-white border border-[#E5E5E5]/60 rounded-3xl p-6 transition-all duration-300 text-left";
  const hoverCls = way.available ? "hover:shadow-xl hover:-translate-y-1.5 hover:border-[#E94F9A]/30" : "opacity-80";

  if (way.action === "donate") {
    return (
      <a href="https://app.autobooks.co/pay/a-girls-girl" target="_blank" rel="noopener noreferrer" data-testid="way-donate" className={`${baseCls} ${hoverCls}`}>{body}</a>
    );
  }
  if (way.action === "shop") {
    return <a href={PRINTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="way-shop" className={`${baseCls} ${hoverCls}`}>{body}</a>;
  }
  if (way.action === "partner") {
    return <Link to="/schools" data-testid="way-partner" className={`${baseCls} ${hoverCls}`}>{body}</Link>;
  }
  if (way.action === "volunteer") {
    return <Link to="/volunteer" data-testid="way-volunteer" className={`${baseCls} ${hoverCls}`}>{body}</Link>;
  }
  if (way.action === "mentor") {
    return <Link to="/mentor" data-testid="way-mentor" className={`${baseCls} ${hoverCls}`}>{body}</Link>;
  }
  return <div data-testid={`way-future-${way.title.toLowerCase().replace(/\s+/g, "-")}`} className={`${baseCls} ${hoverCls} cursor-default`}>{body}</div>;
};

const WaysToGivePage = () => {
  return (
    <div className="py-24 max-w-6xl mx-auto px-6 text-left space-y-24 animate-fade-in" data-testid="ways-to-give-page">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto space-y-5">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"Ways to Give"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
          {"Every Way to Change Her Story"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
          {"Whether through a gift, a partnership, your time, or your voice — there are many meaningful ways to invest in the next generation of confident young women."}
        </p>
        <div className="pt-2">
          <DonateButton label="Give Now" variant="large" showTagline={false} testId="ways-hero-donate" />
        </div>
      </div>

      {/* Ways grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {WAYS.map((w) => <WayCard key={w.title} way={w} />)}
      </div>

      {/* Why your gift matters */}
      <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-14 space-y-10">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Why Your Gift Matters"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Your support doesn't just help girls dream — it equips them to lead."}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {OUTCOMES.map((o) => (
            <div key={o.label} className="bg-white rounded-2xl p-6 text-center space-y-3 border border-[#E5E5E5]/40 hover:-translate-y-1 transition-all">
              <div className="h-12 w-12 rounded-2xl bg-[#FFF5F9] flex items-center justify-center mx-auto">
                <o.icon className="h-5 w-5 text-[#E94F9A]" />
              </div>
              <p className="font-serif text-sm font-semibold text-[#4B1E5A] leading-snug">{o.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Transparency & Trust */}
      <div className="text-center max-w-3xl mx-auto space-y-6" data-testid="transparency-section">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"Transparency & Trust"}</span>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Built on Accountability"}</h2>
        <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
          {"A Girl's Girl is committed to transparency in everything we do. Annual reports, impact reports, and our full 501(c)(3) documentation will be published here as the organization grows — so every supporter can see exactly how their generosity becomes a girl's future."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          {["Annual Reports", "Impact Reports", "501(c)(3) Information"].map((d) => (
            <div key={d} className="border border-dashed border-[#E94F9A]/30 rounded-2xl p-5 bg-white">
              <p className="font-serif text-sm font-semibold text-[#4B1E5A]">{d}</p>
              <p className="font-sans text-[11px] text-[#C8A96A] mt-1">{"Coming Soon"}</p>
            </div>
          ))}
        </div>
        <Link to="/schools" data-testid="ways-partner-cta" className="inline-flex items-center gap-2 border border-[#4B1E5A] text-[#4B1E5A] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[#4B1E5A] hover:text-white transition-all">
          {"Explore Partnerships"} <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

export default WaysToGivePage;
